/**
 * LES MOTS DU JEU. Ce que l'API nomme par un identifiant, le joueur le lit ici.
 *
 * Et surtout : CE QU'UN POINT FAIT. L'écran de création est le plus important du jeu après celui de
 * lecture (design §3) — huit points, définitifs, et le banc de mesure a établi qu'une répartition
 * qui ignore l'Attaque coûte vingt-cinq points de taux de victoire. Ces phrases-là ne sont pas de
 * l'habillage : elles sont la seule chose qui empêche un joueur de découvrir son erreur au
 * troisième combat, alors qu'elle est irrattrapable.
 *
 * Elles disent la RÈGLE, pas une impression. « +4 PV » et « d20 + Attaque contre 10 + Défense » se
 * vérifient dans `packages/engine/src/regles.ts` ; si une valeur change là-bas, elle change ici.
 */
import type { Archetype, Blessure, Caracteristique, ConsommableConnu, Etat, Marqueur, Objet, ObjetConnu, ParcheminConnu, PartieEnVue, Rarete } from "../api/types.js";
import type { NomDeGlyphe } from "../composants/Glyphe.js";
import { CRANS } from "../lecture/taille.js";
import { VITESSES } from "../lecture/voix.js";
import { langue, t, tn, traduit } from "./langue.js";

export interface MotDeCaracteristique {
  nom: string;
  abrege: string;
  /** Ce qu'un point ajoute, en règle, pas en adjectif. */
  parPoint: string;
  /** Ce que la caractéristique décide dans une partie. */
  role: string;
  teinte: string;
}

const CARACTERISTIQUES_FR: Record<Caracteristique, MotDeCaracteristique> = {
  vie: {
    nom: "Vie",
    abrege: "VIE",
    parPoint: "+4 points de vie",
    role: "Ce qui vous sépare de la mort définitive. Rien d'autre.",
    teinte: "var(--sang)"
  },
  attaque: {
    nom: "Attaque",
    abrege: "ATQ",
    parPoint: "+1 au toucher, +1 aux dégâts, +1,5 kg portés",
    role: "Frapper, et faire mal. La seule qui compte deux fois : elle décide si le coup passe, puis de ce qu'il coûte à celui d'en face.",
    teinte: "var(--sang)"
  },
  defense: {
    nom: "Défense",
    abrege: "DEF",
    parPoint: "+1 à la difficulté qu'on doit battre pour vous toucher",
    role: "Ce qui fait qu'un coup vous manque. Un point de plus, c'est un coup sur vingt qui passe à côté.",
    teinte: "var(--essence)"
  },
  agilite: {
    nom: "Agilité",
    abrege: "AGI",
    parPoint: "+1 à l'initiative, à l'esquive, à la fuite et à la dissimulation",
    role: "Frapper le premier, et pouvoir partir. Qui commence a souvent déjà gagné — et fuir à temps, c'est un héros de plus au retour.",
    teinte: "var(--herbe)"
  },
  chance: {
    nom: "Chance",
    abrege: "CHA",
    parPoint: "un critique tous les 4 points (seuil 20, puis 19, 18, 17 — jamais moins)",
    role: "Le coup qui tombe au bon moment, et ce qu'on trouve en fouillant. La plus lente à payer, la seule qui puisse renverser un combat déjà perdu.",
    teinte: "var(--or)"
  },
  essence: {
    nom: "Essence",
    abrege: "ESS",
    parPoint: "+1 d'Essence maximum",
    role: "Le carburant des capacités d'archétype. Sans Essence, un Mage frappe avec un bâton.",
    teinte: "var(--pourpre)"
  }
};

export const ORDRE_DES_CARACTERISTIQUES: Caracteristique[] = ["vie", "attaque", "defense", "agilite", "chance", "essence"];

const ARCHETYPES_FR: Record<Archetype, { nom: string; devise: string }> = {
  guerrier: { nom: "Guerrier", devise: "Il encaisse, et il rend." },
  roublard: { nom: "Roublard", devise: "Il frappe une fois, dans le dos, et ça suffit." },
  mage: { nom: "Mage", devise: "Il paie en Essence ce que les autres paient en sang." },
  rodeur: { nom: "Rôdeur", devise: "Il tire de loin et connaît la forêt." },
  clerc: { nom: "Clerc", devise: "Il tient debout ceux qui devaient tomber." },
  erudit: { nom: "Érudit", devise: "Il regarde d'abord, et frappe où il faut." }
};

/**
 * LES ÉTATS : leur nom, leur effet, et CE QU'ILS FONT AU JOUEUR, à la troisième personne.
 *
 * Le `verbe` sert aux phrases — « un raté vous met en sang » —, le `nom` aux étiquettes. Sans lui,
 * on écrivait « vous laisse saignement », qui n'est pas du français : un nom d'étiquette ne se
 * glisse pas dans une phrase, et une interface qui parle mal se lit comme une interface fausse.
 */
const ETATS_FR: Record<Etat, { nom: string; effet: string; verbe: string }> = {
  saignement: { nom: "Saignement", effet: "2 dégâts par tour. Ne se soigne pas au repos.", verbe: "vous met en sang" },
  poison: {
    nom: "Poison",
    effet: "1 dégât par tour. Ne se soigne pas au repos : il faut un antidote.",
    verbe: "vous empoisonne"
  },
  etourdi: { nom: "Étourdi", effet: "Le tour est perdu.", verbe: "vous étourdit" },
  aveugle: { nom: "Aveugle", effet: "−4 à tous les jets.", verbe: "vous aveugle" },
  terrifie: { nom: "Terrifié", effet: "−2 à tous les jets.", verbe: "vous terrifie" },
  entrave: { nom: "Entravé", effet: "−2 en Agilité, et l'on ne fuit pas.", verbe: "vous entrave" }
};

const BLESSURES_FR: Record<Blessure, { nom: string; effet: string }> = {
  jambe_brisee: { nom: "Jambe brisée", effet: "L'Agilité en pâtit, et la marche coûte plus cher." },
  bras_casse: { nom: "Bras cassé", effet: "L'Attaque en pâtit." },
  oeil_perdu: { nom: "Œil perdu", effet: "Les jets de perception et de tir en pâtissent." },
  cotes_enfoncees: { nom: "Côtes enfoncées", effet: "La Défense en pâtit ; respirer coûte." },
  main_ecrasee: { nom: "Main écrasée", effet: "Tenir une arme à deux mains devient difficile." },
  commotion: { nom: "Commotion", effet: "Tous les jets en pâtissent tant qu'elle dure." }
};

const RARETES_FR: Record<Rarete, { nom: string; teinte: string }> = {
  commun: { nom: "Commun", teinte: "var(--encre-faible)" },
  rare: { nom: "Rare", teinte: "var(--essence)" },
  epique: { nom: "Épique", teinte: "var(--pourpre)" },
  legendaire: { nom: "Légendaire", teinte: "var(--or)" }
};

const EMPLACEMENTS_FR: Record<string, string> = {
  arme_principale: "Arme",
  arme_secondaire: "Main gauche",
  armure: "Armure",
  casque: "Casque",
  bottes: "Bottes",
  amulette: "Amulette",
  anneau: "Anneau",
  anneau_1: "Anneau (I)",
  anneau_2: "Anneau (II)",
  ceinture: "Ceinture",
  cape: "Cape"
};

/**
 * LES CACHES (§3.6) — ce qu'on fouille, et LE VERBE QU'IL FAUT. On ne « fouille » pas quelqu'un de
 * vivant : le geste s'appelle accepter, et c'est la seule chose qui distingue un don d'un cadavre
 * à l'écran. Le libellé de la cache, lui, vient de l'aventure : elle sait comment elle s'appelle.
 */
const CACHES_FR: Record<string, { verbe: string; glyphe: NomDeGlyphe; vide: string }> = {
  cadavre: { verbe: "Fouiller", glyphe: "crane", vide: "Le corps ne porte plus rien." },
  coffre: { verbe: "Forcer", glyphe: "coffre", vide: "Le coffre est vide." },
  cachette: { verbe: "Dégager", glyphe: "cachette", vide: "La cachette est vide." },
  don: { verbe: "Accepter", glyphe: "pnj", vide: "On ne vous tend plus rien." },
  sol: { verbe: "Fouiller", glyphe: "sac", vide: "Il ne reste rien." }
};

/** LES NEUF MARQUEURS DU §4.2, dessinés et NOMMÉS : la forme seule ne dit rien à qui la découvre,
 * et la couleur ne porte jamais l'information seule (accessibilité §5). */
const MARQUEURS_DITS_FR: Record<Marqueur, { nom: string; glyphe: NomDeGlyphe }> = {
  monstre: { nom: "Monstre", glyphe: "monstre" },
  coffre: { nom: "Coffre", glyphe: "coffre" },
  clef: { nom: "Clef", glyphe: "clef" },
  porte: { nom: "Porte verrouillée", glyphe: "verrou" },
  pnj: { nom: "Quelqu'un", glyphe: "pnj" },
  danger: { nom: "Danger", glyphe: "danger" },
  ressource: { nom: "Ressource", glyphe: "ressource" },
  enigme: { nom: "Énigme", glyphe: "enigme" },
  revisiter: { nom: "À revoir", glyphe: "revisiter" }
};

const BIOMES_FR: Record<string, { nom: string; teinte: string }> = {
  foret: { nom: "Forêt", teinte: "var(--herbe)" },
  montagne: { nom: "Montagne", teinte: "var(--encre-douce)" },
  marais: { nom: "Marais", teinte: "var(--essence)" },
  desert: { nom: "Terres sèches", teinte: "var(--or)" },
  ruines: { nom: "Ruines", teinte: "var(--encre-faible)" },
  souterrain: { nom: "Souterrain", teinte: "var(--pourpre)" },
  urbain: { nom: "Ville", teinte: "var(--sang)" },
  cotier: { nom: "Côte", teinte: "var(--essence)" }
};

const MODES_FR: Record<string, { nom: string; promesse: string }> = {
  permadeath: {
    nom: "Permadeath",
    promesse: "Une vie. La mort ferme le héros pour toujours et laisse une trace dans le monde."
  },
  chronicle: {
    nom: "Chronique",
    promesse: "Trois reprises. Chacune coûte une cicatrice : −1 définitif sur une caractéristique que vous choisissez."
  },
  heritage: {
    nom: "Héritage",
    promesse:
      "Le héros meurt pour de bon, mais l'aventure continue : un successeur de la même main reprend à la dernière ancre, avec la moitié de l'or et une relique du défunt. Une option, dépensée au départ."
  }
};

/**
 * LES CATÉGORIES DE L'ÉCHOPPE (§8.2). Chacune dit à quoi elle sert — un intitulé seul (« Confort »)
 * laisse le joueur deviner s'il achète une commodité ou un avantage de jeu, et c'est précisément la
 * question qu'un jeu payant doit répondre avant qu'on la pose.
 */
const CATEGORIES_DE_BOUTIQUE_FR: Record<string, { nom: string; dit: string }> = {
  equipement: {
    nom: "L'équipement",
    dit: "Ce qu'on emporte au corps. Il compte dans l'indice que les aventures regardent à la porte : mieux armé, on entre dans des histoires plus dures — et l'on n'entre plus dans les faciles."
  },
  consommable: {
    nom: "Les consommables",
    dit: "De quoi tenir : manger, se soigner, y voir. Rien de tout cela ne rend plus fort ; ça évite seulement de mourir bêtement."
  },
  option: {
    nom: "Les options d'aventure",
    dit: "Elles se choisissent au départ d'une aventure et s'y dépensent. Une par aventure, jamais rétroactive : on ne rachète pas une mort déjà arrivée."
  },
  confort: {
    nom: "Le confort",
    dit: "Ce qui agrandit le compte pour de bon. Acheté une fois, gardé toujours — même si l'abonnement s'arrête."
  }
};

/** LA CAUSE D'UN MOUVEMENT DE FRAGMENTS. Un solde qui ne se raconte pas est un solde qu'on
 * conteste : chaque ligne du grand livre dit d'où elle vient (§8.1). */
const CAUSES_DE_FRAGMENTS_FR: Record<string, string> = {
  achat_de_pack: "Fragments achetés",
  aventure_terminee: "Aventure terminée",
  boss_majeur: "Boss abattu",
  depense_boutique: "Acheté à l'échoppe",
  remboursement: "Remboursement",
  ajustement: "Ajustement"
};

/** Un prix en centimes, écrit comme un prix. Le serveur ne compte qu'en entiers — un prix en
 * flottant est un prix qui finit par manquer un centime. */
export function prixDit(centimes: number, devise = "eur"): string {
  // LA MISE EN FORME SUIT LA LANGUE, pas le pays : « 4,99 € » en français, « €4.99 » en anglais.
  // C'est ce que la mise en forme du navigateur fait de mieux, et tout ce qu'on lui demande.
  const montant = (centimes / 100).toLocaleString(langue() === "fr" ? "fr-FR" : "en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  if (devise !== "eur") return `${montant} ${devise.toUpperCase()}`;
  return langue() === "fr" ? `${montant} €` : `€${montant}`;
}

/** Une date, telle qu'on la lit dans un livre : sans l'heure, qui ne sert à rien ici. */
export function dateDite(iso: string): string {
  return new Date(iso).toLocaleDateString(langue() === "fr" ? "fr-FR" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}

const REPOS_FR: Record<string, { nom: string; detail: string }> = {
  // SOUFFLER EN PREMIER, parce que c'est le seul qu'on puisse toujours prendre. Le détail dit ce
  // qu'il NE fait pas : sans cela, on le lit comme une halte au rabais au lieu du dernier recours.
  souffle: { nom: "Souffler", detail: "1 h · rien à payer · 5 % des PV · ni fatigue ni essence" },
  halte: { nom: "Halte", detail: "1 h · 1 ration · 10 % des PV · −1 fatigue" },
  long: { nom: "Repos long", detail: "8 h · 2 rations · 40 % des PV · fatigue remise à zéro" },
  camp: { nom: "Camp", detail: "8 h · 2 rations · kit requis · à l'abri des rencontres de nuit" },
  // LE SANCTUAIRE N'APPARAÎT QUE DANS UN SANCTUAIRE (§3.4). C'est le seul repos qui dissipe le
  // poison et le saignement, et le seul qui exige un lieu : l'afficher partout, désaturé, ferait
  // croire à un repos qu'on aurait perdu le droit de prendre.
  sanctuaire: { nom: "Dormir au sanctuaire", detail: "8 h · 1 ration · tout revient · poison et saignement dissipés" }
};

/**
 * CE QUE FAIT UN PARCHEMIN, sur le bouton lui-même (§3.7). Même exigence que le consommable, et
 * même interdit : aucun chiffre n'est recopié ici — le rayon d'une carte et la force d'une
 * bénédiction viennent du serveur, parce qu'ils changent d'un acte à l'autre.
 */
export function ditLeParchemin(p: ParcheminConnu): string {
  switch (p.genre) {
    case "revelation":
      return t("révèle ce qui est caché ici");
    case "cartographie":
      return tn(p.rayon, "porte sur la carte ce qui est à {n} case", "porte sur la carte ce qui est à {n} cases");
    case "rebouteux":
      return t("referme une blessure grave");
    case "purge":
      return t("dissipe tous les états");
    case "repli":
      return t("quitte un combat sans jet — pendant un combat seulement");
    case "benediction": {
      const dits = Object.entries(p.bonus).map(
        ([stat, valeur]) => `${valeur! > 0 ? "+" : ""}${valeur} ${CARACTERISTIQUES[stat as Caracteristique]?.nom ?? stat}`
      );
      return t("{quoi} pendant {h} h", { quoi: dits.join(", "), h: p.heures });
    }
  }
}

/**
 * LE VERBE DU GESTE. « Utiliser » sur une ration est un mot d'inventaire de jeu vidéo ; on mange une
 * ration, on boit une potion, on se panse avec un bandage. Le domaine est français et le jeu est un
 * livre : le verbe juste ne coûte rien et se lit mieux (design §3, « rien de générique »).
 */
export function verbeDeConsommation(c: ConsommableConnu): string {
  if (c.nourrit) return t("Manger");
  if (c.soigne || c.rendEssence) return t("Boire");
  return t("Se panser");
}

/**
 * CE QUE ÇA FAIT, en une ligne, sur le bouton lui-même. Le chiffre vient du serveur et n'est jamais
 * recopié ici : un « +6 PV » écrit en dur dans le front est un mensonge programmé pour le jour où
 * l'on retouchera la table des objets.
 */
export function ditLeConsommable(c: ConsommableConnu): string {
  const dits: string[] = [];
  if (c.soigne) dits.push(t("rend {formule} PV", { formule: c.soigne }));
  if (c.rendEssence) dits.push(t("+{n} essence", { n: c.rendEssence }));
  for (const etat of c.guerit) dits.push(t("soigne {etat}", { etat: ETATS[etat]?.nom.toLowerCase() ?? etat }));
  if (c.nourrit) dits.push(t("un repas"));
  return dits.join(" · ");
}

/** LES SIX CAPS D'UNE GRILLE HEXAGONALE (§4.5), mis en français. Le serveur rend un code — c'est la
 * convention du dépôt et la condition du lot 8 —, et la phrase se compose ici : « l'aiguille tient
 * plein est ». */
const CAPS_FR: Record<string, string> = {
  est: "plein est",
  nord_est: "au nord-est",
  nord_ouest: "au nord-ouest",
  ouest: "plein ouest",
  sud_ouest: "au sud-ouest",
  sud_est: "au sud-est"
};

export const PALIERS_DE_FAIM = ["Rassasié", "Creux", "Affamé", "Épuisé par la faim", "Famine"];

const STATUTS_DE_PARTIE_FR: Record<string, string> = {
  active: "En cours",
  reprise: "Cicatrice à désigner",
  dead: "Mort",
  finished: "Terminée",
  abandoned: "Abandonnée"
};

const GENRES_FR: Record<string, string> = {
  fantasy: "Fantasy",
  post_apocalyptique: "Post-apocalyptique",
  gothique: "Gothique",
  science_fiction: "Science-fiction",
  contemporain: "Contemporain"
};

/**
 * CE QU'ON DIT AU JOUEUR PENDANT QU'ON LUI ÉCRIT UN MONDE. Ce sont les neuf passes du §5, mais on
 * ne les lui nomme pas ainsi : « Passe 6 — le placement, prouvé terminable » décrit notre travail,
 * pas son attente. Ce qu'il veut savoir, c'est où en est SON monde — et qu'on est en train de
 * cacher les clefs qu'il devra trouver.
 */
const ETAPES_DATELIER_FR: Record<string, string> = {
  brief: "On invente le monde",
  bible: "On lui donne ses noms et ses usages",
  topologie: "On dessine la carte",
  structure: "On perce les chemins",
  entites: "On peuple : objets, bêtes et gens",
  placement: "On cache les clefs — et on vérifie qu'on peut ressortir",
  redaction: "On écrit",
  coherence: "On relit tout",
  images: "On commande les images"
};

/* --- Le temps du jeu ------------------------------------------------------------- */

/** L'horloge du §3.4 : un entier d'heures depuis le départ. Jamais l'heure du navigateur. */
export function heureDite(heureDuJour: number): string {
  return t("{h} h", { h: String(heureDuJour).padStart(2, "0") });
}

export function dureeDite(heures: number): string {
  if (heures <= 0) return t("immédiat");
  if (heures < 24) return t("{h} h", { h: heures });
  const jours = Math.floor(heures / 24);
  const reste = heures % 24;
  return reste ? t("{j} j {h} h", { j: jours, h: reste }) : t("{j} j", { j: jours });
}

export function poidsDit(kilos: number): string {
  // La virgule décimale est française ; l'anglais met un point. C'est le seul endroit du front où
  // un séparateur se choisit à la main, et il tient en une ligne.
  const nombre = langue() === "fr" ? kilos.toFixed(1).replace(".", ",") : kilos.toFixed(1);
  return `${nombre} kg`;
}

/**
 * PORTE-T-ON TROP ? Le serveur envoie le détail — le ratio, le malus d'Agilité, l'immobilité —, pas
 * un oui ou un non, et c'est bien ainsi : un ratio de 0,52 n'est pas une alerte. Ce qui en est une,
 * c'est le moment où la charge COÛTE quelque chose, et ce moment se lit ici, une fois.
 */
export function estSurcharge(surcharge: { malusAgilite: number; immobile: boolean }): boolean {
  return surcharge.malusAgilite > 0 || surcharge.immobile;
}

/**
 * LE CATALOGUE D'UN ÉCRAN DE JEU : le socle public, puis les objets que la partie transporte
 * PAR-DESSUS. Une aventure générée invente ses objets ; elle a le dernier mot sur leur nom, et sur
 * tout le reste — deux objets peuvent porter la même clef sans être le même objet.
 */
export function catalogueDe(socle: Objet[], partie?: PartieEnVue | null): Map<string, ObjetConnu> {
  const table = new Map<string, ObjetConnu>();
  for (const o of socle) {
    table.set(o.id, {
      id: o.id,
      nom: o.nom,
      rarete: o.rarete,
      poids: o.poids,
      slots: o.slots,
      emplacement: o.emplacement,
      consommable: o.consommable
        ? {
            soigne: o.consommable.soigne ?? null,
            rendEssence: o.consommable.rendEssence ?? null,
            guerit: o.consommable.guerit ?? [],
            nourrit: o.consommable.nourrit === true
          }
        : null,
      contenant: o.contenant !== undefined
    });
  }
  for (const o of partie?.objets ?? []) {
    table.set(o.id, { ...o, contenant: table.get(o.id)?.contenant ?? false });
  }
  return table;
}

/** Le nom d'un objet quand on ne connaît que sa clef : `epee_courte` → « Épée courte ». Une
 * aventure nomme les siens dans la vue de la partie ; ceci n'est que le dernier recours — et il ne
 * doit jamais rendre « 017 », qui ne veut rien dire pour personne. */
export function nomDObjet(clef: string, catalogue: Map<string, { nom: string }>): string {
  const connu = catalogue.get(clef);
  if (connu) return connu.nom;
  const sansPrefixe = clef.replace(/^(obj|item|cre)_/, "").replace(/_/g, " ").trim();
  const mots = /^\d+$/.test(sansPrefixe) ? `objet ${sansPrefixe}` : sansPrefixe;
  return mots.charAt(0).toUpperCase() + mots.slice(1);
}

/* ================================================================================================
 * CE QUE LES ÉCRANS LISENT (§13).
 *
 * Les tables ci-dessus sont la DONNÉE, en français — la langue dans laquelle ce jeu a été pensé et
 * dans laquelle son brief est écrit. Ce que les écrans importent, ce sont ces copies-ci, dont
 * chaque champ de texte se traduit AU MOMENT OÙ ON LE LIT. Pas un appelant ne change, et il n'y a
 * pas quatre-vingts occasions d'oublier un `t()`.
 * ============================================================================================== */

export const CARACTERISTIQUES: Record<Caracteristique, MotDeCaracteristique> = traduit(CARACTERISTIQUES_FR);
export const ARCHETYPES: Record<Archetype, { nom: string; devise: string }> = traduit(ARCHETYPES_FR);
export const ETATS: Record<Etat, { nom: string; effet: string; verbe: string }> = traduit(ETATS_FR);
export const BLESSURES: Record<Blessure, { nom: string; effet: string }> = traduit(BLESSURES_FR);
export const RARETES: Record<Rarete, { nom: string; teinte: string }> = traduit(RARETES_FR);
export const EMPLACEMENTS: Record<string, string> = traduit(EMPLACEMENTS_FR);
export const CACHES: Record<string, { verbe: string; glyphe: NomDeGlyphe; vide: string }> = traduit(CACHES_FR);
export const MARQUEURS_DITS: Record<Marqueur, { nom: string; glyphe: NomDeGlyphe }> = traduit(MARQUEURS_DITS_FR);
export const BIOMES: Record<string, { nom: string; teinte: string }> = traduit(BIOMES_FR);
export const MODES: Record<string, { nom: string; promesse: string }> = traduit(MODES_FR);
export const CATEGORIES_DE_BOUTIQUE: Record<string, { nom: string; dit: string }> = traduit(CATEGORIES_DE_BOUTIQUE_FR);
export const CAUSES_DE_FRAGMENTS: Record<string, string> = traduit(CAUSES_DE_FRAGMENTS_FR);
export const REPOS: Record<string, { nom: string; detail: string }> = traduit(REPOS_FR);
export const CAPS: Record<string, string> = traduit(CAPS_FR);
export const STATUTS_DE_PARTIE: Record<string, string> = traduit(STATUTS_DE_PARTIE_FR);
export const GENRES: Record<string, string> = traduit(GENRES_FR);
export const ETAPES_DATELIER: Record<string, string> = traduit(ETAPES_DATELIER_FR);

/** CE QU'UN PALIER AJOUTE, en phrases. Un tableau de coches est un argumentaire ; une phrase est
 * une conséquence (design §3). Elles disent la règle — quatre héros, la Chronique — parce que ce
 * sont des arbitrages de jeu, pas des arguments de vente. Elles vivent ici, avec le reste du
 * lexique, pour la même raison que tout le reste : ce qui ne s'inscrit nulle part ne se traduit
 * jamais. */
export const CE_QUE_CA_CHANGE: Record<string, string[]> = {
  chroniqueur: [
    "Quatre héros vivants en même temps, au lieu d'un.",
    "La Chronique : une mort se paie d'une cicatrice — −1 définitif sur une caractéristique que vous choisissez — au lieu de fermer le héros pour toujours. Trois fois, puis c'est fini.",
    "Huit aventures écrites pour vous par mois, au lieu d'une.",
    "La voix sans compte de nœuds, et la carte à exporter en grand."
  ],
  archiviste: [
    "Autant de héros vivants que vous voulez.",
    "Les aventures sans plafond mensuel — trois par jour, le temps qu'elles s'écrivent.",
    "Cinquante lignes de coffre, au lieu de vingt.",
    "Tout ce que le Chroniqueur a, sans les comptes."
  ]
};

/** Les tables en français, telles quelles : le test de couverture des traductions les parcourt
 * pour exiger qu'aucune phrase du lexique ne manque à l'anglais. Les crans de lecture y figurent
 * bien qu'ils vivent ailleurs : ce sont des réglages, mais leurs noms sont des mots. */
export const TABLES_EN_FRANCAIS: Record<string, unknown> = {
  CRANS_DE_TAILLE: CRANS,
  VITESSES_DE_VOIX: VITESSES,
  CE_QUE_CA_CHANGE,
  CARACTERISTIQUES: CARACTERISTIQUES_FR,
  ARCHETYPES: ARCHETYPES_FR,
  ETATS: ETATS_FR,
  BLESSURES: BLESSURES_FR,
  RARETES: RARETES_FR,
  EMPLACEMENTS: EMPLACEMENTS_FR,
  CACHES: CACHES_FR,
  MARQUEURS_DITS: MARQUEURS_DITS_FR,
  BIOMES: BIOMES_FR,
  MODES: MODES_FR,
  CATEGORIES_DE_BOUTIQUE: CATEGORIES_DE_BOUTIQUE_FR,
  CAUSES_DE_FRAGMENTS: CAUSES_DE_FRAGMENTS_FR,
  REPOS: REPOS_FR,
  CAPS: CAPS_FR,
  STATUTS_DE_PARTIE: STATUTS_DE_PARTIE_FR,
  GENRES: GENRES_FR,
  ETAPES_DATELIER: ETAPES_DATELIER_FR,
  PALIERS_DE_FAIM
};
